<script setup>
import { useAlert } from 'dashboard/composables';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  generateTranslationPayload,
  generateLogActionKey,
} from 'dashboard/helper/auditlogHelper';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import format from 'date-fns/format';

const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const records = computed(() => getters['auditlogs/getAuditLogs'].value);
const uiFlags = computed(() => getters['auditlogs/getUIFlags'].value);
const meta = computed(() => getters['auditlogs/getMeta'].value);
const agentList = computed(() => getters['agents/getAgents'].value);
const datePickerKey = ref(0);

const { t } = useI18n();
const route = useRoute();

const routerPage = computed(() => Number(route.query.page ?? 1));
const routerAction = computed(() => route.query.audit_action?.toString() ?? '');
const routerAuditableType = computed(() => route.query.auditable_type?.toString() ?? '');
const routerSource = computed(() => route.query.source?.toString() ?? '');
const routerDateFrom = computed(() => route.query.date_from?.toString() ?? '');
const routerDateTo = computed(() => route.query.date_to?.toString() ?? '');
const routerActor = computed(() => route.query.actor?.toString() ?? '');
const filterState = reactive({
  action: '',
  auditableType: '',
  source: '',
  dateFrom: '',
  dateTo: '',
  actor: '',
});

const actionOptions = computed(() => {
  // Labels are translated for humans, while values stay as backend filter keys.
  return [
    { value: '', label: t('AUDIT_LOGS.FILTERS.ACTIONS.ALL') },
    { value: 'external_event', label: t('AUDIT_LOGS.FILTERS.ACTIONS.EXTERNAL_EVENT') },
    { value: 'create', label: t('AUDIT_LOGS.FILTERS.ACTIONS.CREATE') },
    { value: 'update', label: t('AUDIT_LOGS.FILTERS.ACTIONS.UPDATE') },
    { value: 'destroy', label: t('AUDIT_LOGS.FILTERS.ACTIONS.DESTROY') },
    { value: 'sign_in', label: t('AUDIT_LOGS.FILTERS.ACTIONS.SIGN_IN') },
    { value: 'sign_out', label: t('AUDIT_LOGS.FILTERS.ACTIONS.SIGN_OUT') },
    { value: 'migrate', label: t('AUDIT_LOGS.FILTERS.ACTIONS.MIGRATE') },
  ];
});

const resourceOptions = computed(() => {
  // Resource labels are friendly for the portal while values stay as audited model names for backend filtering.
  return [
    { value: '', label: t('AUDIT_LOGS.FILTERS.RESOURCES.ALL') },
    { value: 'Account', label: t('AUDIT_LOGS.FILTERS.RESOURCES.ACCOUNT') },
    { value: 'AccountUser', label: t('AUDIT_LOGS.FILTERS.RESOURCES.ACCOUNT_USER') },
    { value: 'AutomationRule', label: t('AUDIT_LOGS.FILTERS.RESOURCES.AUTOMATION_RULE') },
    { value: 'Channel::Whatsapp', label: t('AUDIT_LOGS.FILTERS.RESOURCES.WHATSAPP_CHANNEL') },
    { value: 'CoexistenceTemplate', label: t('AUDIT_LOGS.FILTERS.RESOURCES.WHATSAPP_TEMPLATE') },
    { value: 'Contact', label: t('AUDIT_LOGS.FILTERS.RESOURCES.CONTACT') },
    { value: 'Conversation', label: t('AUDIT_LOGS.FILTERS.RESOURCES.CONVERSATION') },
    { value: 'Inbox', label: t('AUDIT_LOGS.FILTERS.RESOURCES.INBOX') },
    { value: 'InboxMember', label: t('AUDIT_LOGS.FILTERS.RESOURCES.INBOX_MEMBER') },
    { value: 'Macro', label: t('AUDIT_LOGS.FILTERS.RESOURCES.MACRO') },
    { value: 'Team', label: t('AUDIT_LOGS.FILTERS.RESOURCES.TEAM') },
    { value: 'TeamMember', label: t('AUDIT_LOGS.FILTERS.RESOURCES.TEAM_MEMBER') },
    { value: 'Template', label: t('AUDIT_LOGS.FILTERS.RESOURCES.TEMPLATE') },
    { value: 'TwilioTemplate', label: t('AUDIT_LOGS.FILTERS.RESOURCES.TWILIO_TEMPLATE') },
    { value: 'User', label: t('AUDIT_LOGS.FILTERS.RESOURCES.USER') },
    { value: 'Webhook', label: t('AUDIT_LOGS.FILTERS.RESOURCES.WEBHOOK') },
  ];
});

const fetchAuditLogs = async ({ page, action, auditableType, source, dateFrom, dateTo, actor }) => {
  try {
    await store.dispatch('auditlogs/fetch', {
      page,
      auditAction: action,
      auditableType,
      source,
      dateFrom,
      dateTo,
      actor,
    });
  } catch (error) {
    const errorMessage = error?.message || t('AUDIT_LOGS.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const generateLogText = auditLogItem => {
  // External audit entries can provide the exact activity text through comment, so prefer it when present.
  if (auditLogItem.comment) {
    return auditLogItem.comment;
  }

  const payload = generateTranslationPayload(auditLogItem, agentList.value);
  const translationKey = generateLogActionKey(auditLogItem);

  // Unknown audit events can still render a neutral activity label instead of leaking empty text.
  if (!translationKey) {
    return `${auditLogItem.auditable_type} ${auditLogItem.action}`;
  }

  const joinIfArray = value => {
    return Array.isArray(value) ? value.join(', ') : value;
  };

  const mergedPayload = {
    ...payload,
    attributes: joinIfArray(payload.attributes),
    values: joinIfArray(payload.values),
  };
  return t(translationKey, mergedPayload);
};

const resolveAuditLogSource = auditLogItem => {
  // Source now lives on audits.source; audited_changes fallback keeps older external records readable.
  return auditLogItem.source || auditLogItem.audited_changes?.source || '-';
};

const resolveAuditLogAction = auditLogItem => {
  const option = actionOptions.value.find(({ value }) => value === auditLogItem.action);
  return option?.label || auditLogItem.action || '-';
};

const resolveAuditLogResource = auditLogItem => {
  const option = resourceOptions.value.find(({ value }) => value === auditLogItem.auditable_type);
  return option?.label || auditLogItem.auditable_type || '-';
};

const syncFilterStateFromRoute = () => {
  // The form mirrors the URL so refreshes and shared links preserve the active audit log filters.
  filterState.action = routerAction.value;
  filterState.auditableType = routerAuditableType.value;
  filterState.source = routerSource.value;
  filterState.dateFrom = routerDateFrom.value;
  filterState.dateTo = routerDateTo.value;
  filterState.actor = routerActor.value;
};

const buildAuditLogQuery = ({ page, action, auditableType, source, dateFrom, dateTo, actor }) => {
  // Blank values are omitted so the URL stays compact and the backend only receives active filters.
  const query = {};

  if (page && page !== 1) {
    query.page = page;
  }

  if (action?.trim()) {
    query.audit_action = action.trim();
  }

  if (auditableType?.trim()) {
    query.auditable_type = auditableType.trim();
  }

  if (source?.trim()) {
    query.source = source.trim();
  }

  if (dateFrom?.trim()) {
    query.date_from = dateFrom.trim();
  }

  if (dateTo?.trim()) {
    query.date_to = dateTo.trim();
  }

  if (actor?.trim()) {
    query.actor = actor.trim();
  }

  return query;
};

const formatDateFilter = date => format(date, 'yyyy-MM-dd');

const applyFilters = () => {
  router.push({
    name: 'auditlogs_list',
    query: buildAuditLogQuery({
      page: 1,
      action: filterState.action,
      auditableType: filterState.auditableType,
      source: filterState.source,
      dateFrom: filterState.dateFrom,
      dateTo: filterState.dateTo,
      actor: filterState.actor,
    }),
  });
};

const clearFilters = () => {
  filterState.action = '';
  filterState.auditableType = '';
  filterState.source = '';
  filterState.dateFrom = '';
  filterState.dateTo = '';
  filterState.actor = '';
  datePickerKey.value += 1;
  router.push({ name: 'auditlogs_list', query: {} });
};

const onDateRangeChange = ([startDate, endDate]) => {
  // The shared SLA date picker emits Date objects; the API expects yyyy-MM-dd query values.
  filterState.dateFrom = formatDateFilter(startDate);
  filterState.dateTo = formatDateFilter(endDate);
};

const onPageChange = page => {
  router.push({
    name: 'auditlogs_list',
    query: buildAuditLogQuery({
      page,
      action: routerAction.value,
      auditableType: routerAuditableType.value,
      source: routerSource.value,
      dateFrom: routerDateFrom.value,
      dateTo: routerDateTo.value,
      actor: routerActor.value,
    }),
  });
};

onMounted(() => {
  store.dispatch('agents/get');
  syncFilterStateFromRoute();
  fetchAuditLogs({
    page: routerPage.value,
    action: routerAction.value,
    auditableType: routerAuditableType.value,
    source: routerSource.value,
    dateFrom: routerDateFrom.value,
    dateTo: routerDateTo.value,
    actor: routerActor.value,
  });
});

watch([routerPage, routerAction, routerAuditableType, routerSource, routerDateFrom, routerDateTo, routerActor], (newValues, oldValues) => {
  syncFilterStateFromRoute();

  if (!oldValues || newValues.some((value, index) => value !== oldValues[index])) {
    fetchAuditLogs({
      page: routerPage.value,
      action: routerAction.value,
      auditableType: routerAuditableType.value,
      source: routerSource.value,
      dateFrom: routerDateFrom.value,
      dateTo: routerDateTo.value,
      actor: routerActor.value,
    });
  }
});

const tableHeaders = computed(() => {
  return [
    t('AUDIT_LOGS.LIST.TABLE_HEADER.ACTIVITY'),
    t('AUDIT_LOGS.LIST.TABLE_HEADER.ACTOR'),
    t('AUDIT_LOGS.LIST.TABLE_HEADER.SOURCE'),
    t('AUDIT_LOGS.LIST.TABLE_HEADER.ACTION'),
    t('AUDIT_LOGS.LIST.TABLE_HEADER.RESOURCE'),
    t('AUDIT_LOGS.LIST.TABLE_HEADER.TIME'),
  ];
});
</script>

<template>
  <div class="flex-1 overflow-auto">
    <BaseSettingsHeader
      :title="$t('AUDIT_LOGS.HEADER')"
      :description="$t('AUDIT_LOGS.DESCRIPTION')"
      feature-name="audit_logs"
    />

    <div class="mt-6 flex-1 text-slate-700 dark:text-slate-300">
      <div
        class="mb-4 rounded-lg border border-slate-100 p-4 dark:border-slate-800"
      >
        <div class="flex flex-col gap-5">
          <!-- Date range stays first so users can scope the audit window before refining by metadata filters. -->
          <div class="border-b border-slate-100 pb-5 text-sm dark:border-slate-800">
            <span class="mb-2 block font-medium text-slate-700 dark:text-slate-300">{{ $t('AUDIT_LOGS.FILTERS.DATE_RANGE') }}</span>
            <woot-date-picker
              :key="datePickerKey"
              :show-footer="false"
              @date-range-changed="onDateRangeChange"
            />
          </div>

          <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:gap-6">
            <div class="grid flex-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <label class="text-sm">
                <span class="mb-1 block font-medium text-slate-700 dark:text-slate-300">{{ $t('AUDIT_LOGS.FILTERS.ACTION') }}</span>
                <!-- Extra right padding prevents the native select arrow from overlapping the selected value. -->
                <select
                  v-model="filterState.action"
                  class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 pr-10 text-slate-900 outline-none transition focus:border-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                >
                  <option
                    v-for="option in actionOptions"
                    :key="option.value || 'all-actions'"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </label>

              <label class="text-sm">
                <span class="mb-1 block font-medium text-slate-700 dark:text-slate-300">{{ $t('AUDIT_LOGS.FILTERS.RESOURCE') }}</span>
                <!-- Values stay as auditable_type keys so the backend receives the canonical model name. -->
                <select
                  v-model="filterState.auditableType"
                  class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 pr-10 text-slate-900 outline-none transition focus:border-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                >
                  <option
                    v-for="option in resourceOptions"
                    :key="option.value || 'all-resources'"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </label>

              <label class="text-sm">
                <span class="mb-1 block font-medium text-slate-700 dark:text-slate-300">{{ $t('AUDIT_LOGS.FILTERS.SOURCE') }}</span>
                <input
                  v-model="filterState.source"
                  type="text"
                  class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none transition focus:border-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                />
              </label>

              <label class="text-sm">
                <span class="mb-1 block font-medium text-slate-700 dark:text-slate-300">{{ $t('AUDIT_LOGS.FILTERS.ACTOR') }}</span>
                <input
                  v-model="filterState.actor"
                  type="text"
                  class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none transition focus:border-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                />
              </label>
            </div>

            <!-- Actions live in a separate container so desktop alignment can be tuned independently from the field grid. -->
            <div class="flex gap-2 lg:shrink-0 lg:pb-0.5">
              <woot-button
                type="button"
                color-scheme="primary"
                @click="applyFilters"
              >
                Filtrar
              </woot-button>
              <woot-button
                type="button"
                variant="smooth"
                color-scheme="secondary"
                @click="clearFilters"
              >
                Limpiar
              </woot-button>
            </div>
          </div>
        </div>
      </div>

      <woot-loading-state
        v-if="uiFlags.fetchingList"
        :message="$t('AUDIT_LOGS.LOADING')"
      />
      <p
        v-else-if="!records.length"
        class="flex flex-col items-center justify-center h-full text-base p-8"
      >
        {{ $t('AUDIT_LOGS.LIST.404') }}
      </p>
      <div v-else class="min-w-full overflow-x-auto">
        <table class="divide-y divide-slate-75 dark:divide-slate-700">
          <thead>
            <th
              v-for="thHeader in tableHeaders"
              :key="thHeader"
              class="py-4 pr-4 text-left font-semibold text-slate-700 dark:text-slate-300"
            >
              {{ thHeader }}
            </th>
          </thead>
          <tbody
            class="divide-y divide-slate-50 dark:divide-slate-800 text-slate-700 dark:text-slate-300"
          >
            <tr v-for="auditLogItem in records" :key="auditLogItem.id">
              <td class="py-4 pr-4 break-all whitespace-nowrap">
                {{ generateLogText(auditLogItem) }}
              </td>
              <td class="py-4 pr-4 break-all whitespace-nowrap">
                {{ auditLogItem.username || '-' }}
              </td>
              <td class="py-4 pr-4 break-all whitespace-nowrap">
                {{ resolveAuditLogSource(auditLogItem) }}
              </td>
              <td class="py-4 pr-4 break-all whitespace-nowrap">
                {{ resolveAuditLogAction(auditLogItem) }}
              </td>
              <td class="py-4 pr-4 break-all whitespace-nowrap">
                {{ resolveAuditLogResource(auditLogItem) }}
              </td>
              <td class="py-4 pr-4 break-all whitespace-nowrap">
                {{
                  messageTimestamp(
                    auditLogItem.created_at,
                    'MMM dd, yyyy hh:mm a'
                  )
                }}
              </td>
            </tr>
          </tbody>
        </table>
        <TableFooter
          :current-page="Number(meta.currentPage)"
          :total-count="meta.totalEntries"
          :page-size="meta.perPage"
          class="border-slate-50 dark:border-slate-800 border-t !px-0 py-4"
          @page-change="onPageChange"
        />
      </div>
    </div>
  </div>
</template>
